import { PrepareApiProcedure } from '../../components/common/utils/prepare-api-procedure';
import {
  ApiRequest,
  fetchDataFromAPI,
  fetchDataFromAPITyped,
  isSuccessful,
} from '../../services/api-requests';
import { UserAttributes } from '../../models/login-register-model';
import { UserSessionAttributes } from '../../models/user-session-model';
import { notificationService } from '../../services/notification-service';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { UserInfo } from '../../models/user-info';
import { userActions } from '../slices/user-slice';
import { storeUserSession } from '../../services/helpers/session-helpers';
import { AppDispatch, RootState } from '..';
import { isEqual } from 'lodash';
import { getUserActiveCharges } from '../../services/charger-service';
import { UserPlanAssociation } from '../../models/user-plan-associations-model';
import { useDispatch } from 'react-redux';
import { getUserInfo, updateUserToServer } from '../../services/user-service';

export const validateUserToken = async () => {
  try {
    // Build typed ApiRequest
    const apiRequest: ApiRequest = {
      route: 'user/validate',
      request: {
        method: 'GET',
        entity: 'validate',
        body: undefined,
      },
    };

    const typedResponse = await fetchDataFromAPITyped<UserSessionAttributes>(
      apiRequest
    );

    // Return the typed response so caller can check status
    return typedResponse;
  } catch (error) {
    console.error('Error validating user: ', error);
    // Return error response in same format for consistency
    return {
      status: 500,
      payload: null as unknown as UserSessionAttributes,
    };
  }
};

// Login User
export const loginUser = async (userLogin: UserSessionAttributes) => {
  try {
    if (userLogin.access_token !== '') {
      //get the user object here
      const userObj = await getUserInfo();
      if (isSuccessful(userObj.status)) {
        return userObj.payload;
      } else {
        notificationService.sendNotification(
          'error',
          'Unable to get user info'
        );
      }
    }

    const { logintype, service } = userLogin;
    
    // Determine the appropriate endpoint based on authentication type
    let endpoint = 'login';
    let requestBody: any = userLogin;
    
    // If this is OAuth authentication (apple or google), use the OAuth endpoint
    if (logintype === 'google' || service === 'google') {
      endpoint = 'google/oauth2/mobile';
      // For OAuth, we need to send the OAuth-specific data
      requestBody = {
        email: userLogin.email,
        service: service || logintype,
        sub: userLogin.sub,
        IpAddress: userLogin.IpAddress
      };
    } else if (logintype === 'apple' || service === 'apple') {
      endpoint = 'login/oauth';
      // For OAuth, we need to send the OAuth-specific data
      requestBody = {
        email: userLogin.email,
        service: service || logintype,
        sub: userLogin.sub,
        IpAddress: userLogin.IpAddress
      };
    }
    
    // console.log('Selected endpoint:', endpoint);
    // console.log('Request body:', JSON.stringify(requestBody, null, 2));

    // Build typed ApiRequest and use the shared fetch helper
    const apiRequest: ApiRequest = {
      route: endpoint,
      request: {
        method: 'POST',
        entity: 'login',
        body: requestBody,
      },
    };

    const typedResponse = await fetchDataFromAPITyped<UserSessionAttributes>(
      apiRequest
    );

    if (!isSuccessful(typedResponse.status)) {
      const errorData: any = typedResponse.payload;
      if (errorData?.statusNum === '3003') {
        // reserved: handle device limit if needed
      } else {
        if (logintype !== 'google') {
          if (errorData) {
            console.log(errorData);
          }
          errorData?.message === 'Password is incorrect!'
            ? notificationService.sendNotification('error', `Password is incorrect`)
            : errorData?.message === 'Username is incorrect!'
            ? notificationService.sendNotification('error', `Username is incorrect!`)
            : notificationService.sendNotification('error', `Invalid Credentials`);
        } else {
          notificationService.sendNotification('error', errorData?.error || 'Login failed');
        }
      }
      return null;
    }
    // This is now the login data
    const data = typedResponse.payload as UserSessionAttributes;

    //console.log("received login information:", data);

    //clear the AsyncStorage

    const keys = await AsyncStorage.getAllKeys();
    await AsyncStorage.multiRemove(keys);

    if (data.message === 'Maximum number of devices used!') {
      notificationService.sendNotification(
        'error',
        `Maximum number of devices used!`
      );
    } else if ((Array.isArray(data.role) ? data.role : [data.role]).includes('guest')) {
      notificationService.sendNotification('success', `Welcome :)`);
    } else if (data) {
      //notificationService.sendNotification("success", `Login Successful :)`);
    } else {
      notificationService.sendNotification('error', `Invalid Credentials`);
    }

    const IpAddress = data.IpAddress;
    const access_token = data.access_token;
    const email = data.email;
    const role = data.role;
    const username = data.username;

    await AsyncStorage.setItem('token', access_token);
    await AsyncStorage.setItem('IpAddress', `${IpAddress ?? ''}`);
    await AsyncStorage.setItem('email', email);
    await AsyncStorage.setItem('username', username);
    // await AsyncStorage.setItem('role', JSON.stringify(role));
    await AsyncStorage.setItem('userId', data.id);
    await storeUserSession(data);

    return data;
  } catch (e) {
    console.error(e);
  }
};

//register user
export const registerUser = async (userRegistration: UserAttributes) => {
  try {
    const { service } = userRegistration;
    
    // Determine the appropriate endpoint based on registration type
    let endpoint = 'user/register';
    let requestBody: any = userRegistration;
    
    // If this is OAuth registration (apple or google), use the OAuth endpoint
    if (service === 'apple' || service === 'google') {
      endpoint = 'user/register/oauth';
      // For OAuth, we need to send the OAuth-specific data
      requestBody = {
        email: userRegistration.email,
        service: service,
        sub: userRegistration.password, // The password field contains the OAuth sub
        IpAddress: '1.1.1.1', // Default IP address for OAuth registration
        first_name: userRegistration.first_name,
        last_name: userRegistration.last_name
      };
    }

    const apiQuery = PrepareApiProcedure(
      endpoint,
      'POST',
      'register',
      requestBody
    );
    //console.log("sending request to", process.env.REACT_NATIVE_API_BASE_URL + apiQuery.api)
    const response = await fetch(
      process.env.REACT_NATIVE_API_BASE_URL + apiQuery.api,
      {
        method: apiQuery.request.action,
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
        body:
          apiQuery.request.data === ''
            ? null
            : JSON.stringify(apiQuery.request.data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error(errorData);
      return errorData;
    }
    const uuid = await response.json();
    const data = { uuid: uuid.uuid, status: response.status };

    //console.log(data);
    return data;
  } catch (e) {
    console.error(e);
  }
};

export const oauthLogin = (profile: UserInfo) => {
  return async (dispatch: AppDispatch) => {
    //should we be checking the server for registration here?
    // or grabbing the server registration?

    dispatch(
      userActions.loginUser({
        profile: profile,
      })
    );
  };
};

// update the user's profile
/**
 * DEPRECATED: Use updateUserLocally and updateUserOnServer for optimistic UI updates.
 */
export const updateUser = (
  sessionObj: UserSessionAttributes,
  updates: {
    first_name?: string;
    last_name?: string;
    email?: string;
    password?: string;
    primary_use?: string;
    host_enabled?: boolean;
  }
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<boolean> => {
    try {
      const accountInfo = { ...updates };
      // Filter out any undefined values
      const filteredAccountInfo = Object.fromEntries(
        Object.entries(accountInfo).filter(([_, value]) => value !== undefined)
      );
      // const apiQuery = PrepareApiProcedure(
      //   'user/update',
      //   'PUT',
      //   'user',
      //   filteredAccountInfo
      // );
      const response = await updateUserToServer(filteredAccountInfo);
      // Guard: do not update session if a mode change is in progress
      const isModeChanging = getState().user.isModeChanging;
      if (isModeChanging) {
        console.log(
          'updateUser: Skipping session update (mode change in progress)'
        );
        return isSuccessful(response.status);
      }
      // Only update the session fields that were changed, never overwrite primary_use unless it was updated
      if (isSuccessful(response.status)) {
        const currentSession = getState().user.session;
        let newSession = { ...currentSession };
        Object.entries(filteredAccountInfo).forEach(([key, value]) => {
          if (key === 'primary_use') {
            // Only update primary_use if it was part of the update
            newSession.primary_use = value as string;
          } else if (key in newSession) {
            // Update other fields
            (newSession as any)[key] = value;
          }
        });
        dispatch(
          userActions.setUserSession({
            session: newSession,
          })
        );
        // Persist updated session so future rehydration keeps names and other fields
        try {
          await storeUserSession(newSession as UserSessionAttributes);
        } catch {}
        return true;
      } else {
        const errMsg = (response as any)?.errorPayload?.message || (response.payload as any)?.message || 'Failed to update user';
        notificationService.sendNotification('error', errMsg);
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  };
};

// Optimistically update the user session in Redux only
export const updateUserLocally = (
  sessionObj: UserSessionAttributes,
  updates: {
    primary_use?: string;
    host_enabled?: boolean;
    first_name?: string;
    last_name?: string;
    email?: string;
    password?: string;
  }
) => {
  return (dispatch: AppDispatch) => {
    dispatch(userActions.setIsModeChanging(true));
    dispatch(
      userActions.setUserSession({
        session: { ...sessionObj, ...updates },
      })
    );
  };
};

// Make the server call in the background and verify the change
export const updateUserOnServer = (
  sessionObj: UserSessionAttributes,
  updates: {
    primary_use?: string;
    host_enabled?: boolean;
    first_name?: string;
    last_name?: string;
    email?: string;
    password?: string;
  },
  revertCallback?: () => void
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<boolean> => {
    try {
      const currState = getState();
      const accountInfo = { ...updates };
      const filteredAccountInfo = Object.fromEntries(
        Object.entries(accountInfo).filter(([_, value]) => value !== undefined)
      );
      const response = await updateUserToServer(filteredAccountInfo);
      //const newSession = await validateUserToken();
      //console.log('newSession =', newSession);
      if (isSuccessful(response.status)) {
        dispatch(
          userActions.setUserSession({
            session: {
              ...currState.user.session,
              primary_use: updates.primary_use,
            },
          })
        );
        // Use requestAnimationFrame to ensure this runs after the next render cycle
        // This gives time for all useEffect hooks to complete their execution
        requestAnimationFrame(() => {
          setTimeout(() => {
            dispatch(userActions.setIsModeChanging(false));
          }, 50);
        });
        return true;
      } else {
        notificationService.sendNotification(
          'error',
          (response.status ? `Status: ${response.status}` : '') +
            ' ' +
            (JSON.stringify(response) || 'Failed to update user')
        );
        if (revertCallback) revertCallback();
        dispatch(userActions.setIsModeChanging(false));
      }
      return false;
    } catch (error) {
      notificationService.sendNotification(
        'error',
        'Failed to update user mode'
      );
      if (revertCallback) revertCallback();
      dispatch(userActions.setIsModeChanging(false));
      return false;
    }
  };
};

export const getActiveCharges = () => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      //const apiQuery = PrepareApiProcedure("user/getActiveCharges", "GET", "user", '');
      //const response = await fetchDataFromAPI(apiQuery);
      const response = (await getUserActiveCharges()).payload;
      //console.log("got active charge response: ", response);

      // Get the current activeCharges from the Redux store
      const currentActiveCharges = getState().user.activeCharges;

      // Check if the response is an empty array
      if (Array.isArray(response) && response.length === 0) {
        // Dispatch to clear the activeCharges if the response is an empty array
        dispatch(userActions.clearActiveCharges());
      } else if (Array.isArray(response)) {
        // Compare new response with the current activeCharges
        if (!isEqual(currentActiveCharges, response)) {
          // If there is a difference, dispatch to set activeCharges in the store
          dispatch(userActions.setActiveCharges({ activeCharges: response }));
        }
      } else {
        // Handle unexpected response types (e.g., errors or non-array responses)
        console.error('Unexpected response format:', response);
        // console.trace();
      }
    } catch (error) {
      console.error('Failed to fetch active charges:', error);
    }
  };
};

//FUNCTION: getDriverPlan
//PURPOSE: retrieve details about the user's active payment plan
export const getDriverPlan = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const apiRequest: ApiRequest = {
        route: `user/getDriverPlanInfo`,
        request: {
          method: 'GET',
          entity: 'userPlan',
          body: undefined,
        },
      };
      const driverPlan = await fetchDataFromAPITyped<UserPlanAssociation>(
        apiRequest
      );
      if (isSuccessful(driverPlan.status)) {
        dispatch(
          userActions.setDriverPlan({
            driverPlan: driverPlan.payload,
          })
        );
      }
    } catch (error) {
      console.log('Unable to get driver plan', error);
    }
  };
};

//FUNCTION: getHostPlan
//PURPOSE: retrieve details about the user's active host plan and store it
//         in Redux so we can determine free-tier status (hostPlan.price === 0)
export const getHostPlan = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const apiRequest: ApiRequest = {
        route: `user/getHostPlanInfo`,
        request: {
          method: 'GET',
          entity: 'userPlan',
          body: undefined,
        },
      };
      const hostPlan = await fetchDataFromAPITyped<UserPlanAssociation>(
        apiRequest
      );
      if (isSuccessful(hostPlan.status)) {
        dispatch(
          userActions.setHostPlan({
            hostPlan: hostPlan.payload,
          })
        );
      }
    } catch (error) {
      console.log('Unable to get host plan', error);
    }
  };
};
